import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Inicio } from './Inicio.js';
import { Puente, pareceEscritorio } from './Puente.js';
import { Permisos } from './Permisos.js';
import { Escaneo, type Capturas } from './Escaneo.js';
import { Gestos, PruebaNoCompletada } from './Gestos.js';
import { Verificando } from './Verificando.js';
import { Resultado } from './Resultado.js';
import { aplicarMarca } from './marca.js';
import {
  enviarEvidencia,
  enviarGestos,
  portada as traerPortada,
  SesionNoDisponible,
  type EvidenciaDelCliente,
  type Portada,
} from './api.js';
import './estilos.css';

/**
 * El arranque, y nada más.
 *
 * Aquí NO va lógica de negocio: este archivo llama a `createRoot()` al importarse, así que
 * cualquier cosa que viva aquí es imposible de probar — un test que lo importe revienta antes
 * de empezar, sin DOM. Ya pasó con `contrastaConBlanco`, y por eso la marca está en
 * `marca.ts`. Lo que sí vive aquí es el CABLEADO: qué pantalla sigue a cuál.
 *
 * ── LA MÁQUINA ES documento → gestos-con-selfie → desenlace ────────────────────
 *
 * Ya no hay pantalla de Rostro: los gestos capturan el selfie en el gesto 'frente' (ver la
 * cabecera de Gestos.tsx). Una sola sesión de cámara de cara, una pantalla menos.
 *
 * ── EL PASO NO VA EN LA URL, Y ES DELIBERADO ───────────────────────────────────
 *
 * Es tentador poner `/v/:token/gestos` y tener rutas bonitas. Sería un error por dos motivos:
 *
 * El botón "atrás" del navegador dejaría a la persona en una cámara a la que nunca se le pidió
 * permiso, en un estado que el código no espera.
 *
 * Y peor: cualquiera podría escribir la URL de los gestos a mano y **saltarse el escaneo del
 * documento**. El paso es una máquina de estados, no una dirección — y las máquinas de estados
 * no se navegan escribiendo.
 *
 * ── EL ORDEN DEL ENVÍO ES LÓGICA, NO ESTILO (#79) ──────────────────────────────
 *
 * Los GESTOS se resuelven PRIMERO y la EVIDENCIA viaja DESPUÉS. Si fuera al revés, un fallo
 * en los gestos dejaría a la persona con derecho a reintentar… contra una sesión que la
 * evidencia ya completó — y una sesión completada no se reabre (core/sesion.ts). El orden es
 * lo que hace que el reintento de gestos exista.
 */
function tokenDeLaRuta(): string | null {
  const m = /^\/v\/([A-Za-z0-9_-]+)/.exec(window.location.pathname);
  return m?.[1] ?? null;
}

function App(): JSX.Element {
  const [token] = useState(tokenDeLaRuta);
  const [portada, setPortada] = useState<Portada | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [empezo, setEmpezo] = useState(false);
  const [sigueAqui, setSigueAqui] = useState(false);
  const [permitido, setPermitido] = useState(false);
  const [capturas, setCapturas] = useState<Capturas | null>(null);

  // La prueba de vida activa y el envío. `reintento` es la key de <Gestos>: subirla lo
  // remonta desde cero, y remontarlo es pedir OTRO reto — el servidor cobra el intento.
  const [enviando, setEnviando] = useState(false);
  const [rechazo, setRechazo] = useState<string | null>(null);
  const [reintento, setReintento] = useState(0);
  const [evidenciaFallida, setEvidenciaFallida] = useState<EvidenciaDelCliente | null>(null);
  const [desenlace, setDesenlace] = useState<'verificado' | 'no_verificado' | 'pendiente' | null>(
    null,
  );

  useEffect(() => {
    if (token === null) {
      setError('Este enlace no es válido. Pídele uno nuevo a quien te lo envió.');
      return;
    }

    traerPortada(token)
      .then((p) => {
        aplicarMarca(p.marca);
        setPortada(p);
      })
      .catch((e: unknown) => {
        setError(
          e instanceof SesionNoDisponible
            ? e.message
            : // Cualquier otro fallo —red, servidor— se cuenta igual: a la persona, "no se
              // pudo abrir" es lo único accionable. Un mensaje técnico no le sirve y la asusta.
              'No pudimos abrir esta verificación. Revisa tu conexión e inténtalo de nuevo.',
        );
      });
  }, [token]);

  if (error !== null) {
    return (
      <div className="flex min-h-full items-center justify-center px-8 text-center">
        <p className="max-w-xs text-[15px] leading-relaxed text-tinta-suave">{error}</p>
      </div>
    );
  }

  if (portada === null || token === null) {
    // Sin spinner de marca: todavía no sabemos de quién es la marca. Poner el logo nuestro
    // aquí sería enseñarle a la persona una marca que no esperaba, y que además no es la de
    // quien le pidió verificarse.
    return <div className="min-h-full bg-fondo" />;
  }

  // El envío de la evidencia (#79). Es seguro de reintentar: una sesión con veredicto no se
  // re-evalúa, el servidor devuelve el desenlace que ya existe.
  const mandarEvidencia = (ev: EvidenciaDelCliente): void => {
    setEnviando(true);
    enviarEvidencia(token, ev)
      .then((r) => {
        setEnviando(false);
        setDesenlace(r.desenlace);
      })
      .catch((e: unknown) => {
        setEnviando(false);
        if (e instanceof SesionNoDisponible) {
          setError(e.message);
        } else {
          // Red caída a mitad de subida: se guarda el paquete y se ofrece reintentar el
          // ENVÍO — no los gestos, que ya se resolvieron y no se pueden repetir gratis.
          setEvidenciaFallida(ev);
        }
      });
  };

  if (!empezo) {
    return <Inicio portada={portada} onEmpezar={() => setEmpezo(true)} />;
  }

  // El puente escritorio→celular. Solo cuando el dispositivo PARECE de escritorio, y con
  // salida siempre: la heurística puede equivocarse y hay gente cuya única cámara es la
  // webcam. El QR es el mismo enlace — el salto de dispositivo es legal por diseño
  // (core/sesion.ts: lo que protege es de quién es la cara, no qué navegador la mira).
  if (!sigueAqui && pareceEscritorio()) {
    return <Puente onContinuarAqui={() => setSigueAqui(true)} onVolver={() => setEmpezo(false)} />;
  }

  // La hoja de PERMISOS (#74). No pide nada: prepara el diálogo del navegador, que salta en
  // la pantalla siguiente, donde la cámara de verdad se enciende.
  if (!permitido) {
    return <Permisos onContinuar={() => setPermitido(true)} onVolver={() => setEmpezo(false)} />;
  }

  if (capturas === null) {
    return <Escaneo token={token} onListo={setCapturas} onVolver={() => setPermitido(false)} />;
  }

  // Con el documento capturado, el siguiente y último tramo son los GESTOS, que además
  // capturan el selfie (el 'frente' es la foto del rostro — ver Gestos.tsx). Ya no hay
  // pantalla intermedia de Rostro.
  if (desenlace === null) {
    // VERIFICANDO (#75) cubre una espera REAL: los POST de gestos y evidencia en vuelo.
    if (enviando) {
      return <Verificando />;
    }

    if (rechazo !== null) {
      return (
        <PruebaNoCompletada
          mensaje={rechazo}
          onReintentar={() => {
            setRechazo(null);
            setReintento((n) => n + 1);
          }}
        />
      );
    }

    if (evidenciaFallida !== null) {
      return (
        <PruebaNoCompletada
          mensaje="No pudimos enviar tu verificación. Revisa tu conexión e inténtalo de nuevo."
          onReintentar={() => {
            const ev = evidenciaFallida;
            setEvidenciaFallida(null);
            mandarEvidencia(ev);
          }}
        />
      );
    }

    return (
      <Gestos
        key={reintento}
        token={token}
        // Volver desde los gestos tira las capturas del documento: volver es volver a
        // capturar. Conservarlas invitaría a mezclar un documento viejo con un rostro nuevo.
        onVolver={() => setCapturas(null)}
        onNoDisponible={setError}
        onCompletado={(intentos, selfie, fotogramas, fps) => {
          setEnviando(true);
          // GESTOS PRIMERO. Ver el comentario de cabecera: el orden es lo que permite
          // reintentar la prueba sin haber quemado la sesión con la evidencia.
          enviarGestos(token, intentos)
            .then((r) => {
              if (r.estado === 'fallada' || r.estado === 'sospechosa') {
                setEnviando(false);
                // El mensaje es del servidor y ya está escrito para la persona.
                setRechazo(r.mensaje);
                return;
              }
              mandarEvidencia({
                anverso: capturas.anverso,
                reverso: capturas.reverso,
                // El selfie viene de los propios gestos: es el fotograma del 'frente' a
                // resolución completa. El payload no cambia; cambia quién produce la foto.
                rostro: selfie,
                fotogramas,
                fps,
              });
            })
            .catch((e: unknown) => {
              setEnviando(false);
              setError(
                e instanceof SesionNoDisponible
                  ? e.message
                  : 'No pudimos completar la verificación. Revisa tu conexión e inténtalo de nuevo.',
              );
            });
        }}
      />
    );
  }

  // ── EL DESENLACE ES EL DEL SERVIDOR ─────────────────────────────────────────
  //
  // Verificado exige que el servidor haya visto los píxeles Y los datos: rostro que
  // coincide, número que coincide, cotejo contra RENIEC o lo declarado, vida evaluada.
  // No verificado exige convergencia de señales. Todo lo demás es pendiente: lo mira
  // un humano.
  return (
    <Resultado
      desenlace={desenlace}
      paraQuien={portada.paraQuien}
      logoUrl={portada.marca.logoUrl}
    />
  );
}

createRoot(document.getElementById('raiz')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
